import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import { useBrand } from "../../lib/BrandContext";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import PasswordInput from "../../components/PasswordInput";

export default function ResetPassword() {
  const brand = useBrand();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [token] = useState(params.get("token") || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      navigate("/login");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  // If the parent didn't arrive with a ?token=… URL, there's no path
  // forward — the token comes from the reset email link. Guide them
  // back to Forgot Password instead of showing a manual-entry field.
  if (!token) {
    return (
      <div className="flex min-h-screen bg-ink-100/40">
        <div className="m-auto w-full max-w-md px-6 py-10">
          <Link to="/" className="flex items-center gap-2 font-heading text-xl font-bold text-ink-900">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-white"><ShieldCheck className="h-5 w-5" /></span>
            {brand.app_name}
          </Link>
          <div className="mt-8 rounded-2xl border border-ink-100 bg-white p-8 shadow-sm" data-testid="reset-missing-token">
            <h1 className="font-heading text-2xl font-bold text-ink-900">Reset link needed</h1>
            <p className="mt-3 text-sm text-ink-600">
              This page needs a reset link from your inbox. If you don&apos;t
              have one yet, request a new password reset email.
            </p>
            <Link to="/forgot-password">
              <Button className="mt-6 h-12 w-full bg-brand font-semibold text-white hover:bg-brand-hover" data-testid="reset-request-new-link">
                Request a reset email
              </Button>
            </Link>
            <p className="mt-4 text-center text-sm">
              <Link to="/login" className="font-semibold text-brand hover:underline">Back to sign in</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-ink-100/40">
      <div className="m-auto w-full max-w-md px-6 py-10">
        <Link to="/" className="flex items-center gap-2 font-heading text-xl font-bold text-ink-900">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-white"><ShieldCheck className="h-5 w-5" /></span>
          {brand.app_name}
        </Link>
        <div className="mt-8 rounded-2xl border border-ink-100 bg-white p-8 shadow-sm">
          <h1 className="font-heading text-2xl font-bold text-ink-900">Set a new password</h1>
          <p className="mt-2 text-sm text-ink-500">Choose a new password for your account.</p>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="password">New password</Label>
              <PasswordInput id="password" minLength={6} required autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 h-12" data-testid="reset-password-input" capsWarningTestId="reset-caps-warning" />
            </div>
            {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p> : null}
            <Button type="submit" disabled={loading} className="h-12 w-full bg-brand font-semibold text-white hover:bg-brand-hover" data-testid="reset-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
